// Base
import { BaseApi } from 'api/base.api'

// Interface
import {
  IAssessmentAnotherDocumentApi,
  IAssessmentAnotherDocumentApiRequest,
  IAssessmentAnotherDocumentApiGetListAttrs,
  IAssessmentAnotherDocumentApiListResponse,
  IAssessmentAnotherDocumentApiDownloadDocumentAttrs,
  IAssessmentAnotherDocumentApiDownloadDocumentResponse,
  IAssessmentAnotherDocumentApiCreateAttrs,
  IAssessmentAnotherDocumentApiDetailResponse,
  IAssessmentAnotherDocumentApiGetDetailAttrs,
  IAssessmentAnotherDocumentApiUpdateAttrs,
  IAssessmentAnotherDocumentApiDeleteAttrs,
  IAssessmentAnotherDocumentApiNullResponse,
  IAssessmentAnotherDocumentApiUploadDocumentAttrs,
  IAssessmentAnotherDocumentApiUploadDocumentResponse,
  IAssessmentAnotherDocumentApiCreateUpdateResponse,
} from 'interfaces/back-office/assessment/another-document/another-document-api.interface'

// Constant
import { ApiConstantHc, ApiConstantHcBase } from 'constant'

// Utils
import { axios_renderHcUrl } from 'utils'

export class AssessmentAnotherDocumentApi
  extends BaseApi<IAssessmentAnotherDocumentApi>
  implements IAssessmentAnotherDocumentApiRequest
{
  readonly url = ApiConstantHc.ASSESSMENT_ANOTHER_DOCUMENT_URL
  private assessmentAnotherDocumentUrl = axios_renderHcUrl(this.url, ApiConstantHcBase.ASSESSMENT)

  /**
   * @description Get assessment another document list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiGetListAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiListResponse>} Promise<IAssessmentAnotherDocumentApiListResponse>
   */
  async assessmentAnotherDocument_getList(
    payload: IAssessmentAnotherDocumentApiGetListAttrs,
  ): Promise<IAssessmentAnotherDocumentApiListResponse> {
    return this.POST(`${this.assessmentAnotherDocumentUrl}/list/${payload.params.assessmentId}`, payload.body, {
      ...payload.requestConfig,
    })
  }

  /**
   * @description Create assessment another document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiCreateAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiCreateUpdateResponse>} Promise<IAssessmentAnotherDocumentApiCreateUpdateResponse>
   */
  async assessmentAnotherDocument_create(
    payload: IAssessmentAnotherDocumentApiCreateAttrs,
  ): Promise<IAssessmentAnotherDocumentApiCreateUpdateResponse> {
    return this.POST(`${this.assessmentAnotherDocumentUrl}/create/${payload.params.assessmentId}`, payload.body, {
      ...payload.requestConfig,
    })
  }

  /**
   * @description Get assessment another document detail
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiGetDetailAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiDetailResponse>} Promise<IAssessmentAnotherDocumentApiDetailResponse>
   */
  async assessmentAnotherDocument_getDetail(
    payload: IAssessmentAnotherDocumentApiGetDetailAttrs,
  ): Promise<IAssessmentAnotherDocumentApiDetailResponse> {
    return this.POST(`${this.assessmentAnotherDocumentUrl}/${payload.params.anotherDocumentId}`, {}, { ...payload.requestConfig })
  }

  /**
   * @description Update assessment another document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiUpdateAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiCreateUpdateResponse>} Promise<IAssessmentAnotherDocumentApiCreateUpdateResponse>
   */
  async assessmentAnotherDocument_update(
    payload: IAssessmentAnotherDocumentApiUpdateAttrs,
  ): Promise<IAssessmentAnotherDocumentApiCreateUpdateResponse> {
    return this.POST(`${this.assessmentAnotherDocumentUrl}/update/${payload.params.anotherDocumentId}`, payload.body, {
      ...payload.requestConfig,
    })
  }

  /**
   * @description Delete assessment another document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiDeleteAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiNullResponse>} Promise<IAssessmentAnotherDocumentApiNullResponse>
   */
  async assessmentAnotherDocument_delete(
    payload: IAssessmentAnotherDocumentApiDeleteAttrs,
  ): Promise<IAssessmentAnotherDocumentApiNullResponse> {
    return this.POST(
      `${this.assessmentAnotherDocumentUrl}/delete/${payload.params.anotherDocumentId}`,
      {},
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Download document of another document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiDownloadDocumentAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiDownloadDocumentResponse>} Promise<IAssessmentAnotherDocumentApiDownloadDocumentResponse>
   */
  async assessmentAnotherDocument_downloadDocument(
    payload: IAssessmentAnotherDocumentApiDownloadDocumentAttrs,
  ): Promise<IAssessmentAnotherDocumentApiDownloadDocumentResponse> {
    return this.POST(
      `${this.assessmentAnotherDocumentUrl}/download/${payload.params.blobId}`,
      {},
      { ...payload.requestConfig, responseType: 'blob' },
    )
  }

  /**
   * @description Upload document of another document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentAnotherDocumentApiUploadDocumentAttrs} payload
   *
   * @return {Promise<IAssessmentAnotherDocumentApiUploadDocumentResponse>} Promise<IAssessmentAnotherDocumentApiUploadDocumentResponse>
   */
  async assessmentAnotherDocument_uploadDocument(
    payload: IAssessmentAnotherDocumentApiUploadDocumentAttrs,
  ): Promise<IAssessmentAnotherDocumentApiUploadDocumentResponse> {
    const formData = new FormData()

    formData.append('document', payload.body.document)

    return this.POST(`${this.assessmentAnotherDocumentUrl}/uploadDocument/${payload.params.anotherDocumentId}`, formData, {
      ...payload.requestConfig,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }
}
